/**
 * Camada de tracking (Google Ads + Meta Pixel + GTM dataLayer).
 * Conversões de funil e PageView virtual no SPA.
 */

import { toE164Digits } from './phone';
import { pickAttribution, sendLifecycleEvent } from './lifecycleEvents';

const UTM_STORAGE_KEY = 'dd_utm_params';
const WA_REF_STORAGE_KEY = 'dd_wa_ref';
const DEDUPE_PREFIX = 'dd_track_';

/**
 * Tag do Google Ads e rótulos das ações de conversão.
 *
 * Cada rótulo é a parte após a barra no `send_to` gerado pelo Google Ads
 * (ex.: em `AW-17677434119/AbC-D_efG`, o rótulo é `AbC-D_efG`).
 * Sem o rótulo configurado a conversão correspondente não é enviada —
 * isso evita disparar eventos para ações que ainda não existem na conta.
 */
const GOOGLE_ADS_ID = process.env.REACT_APP_GADS_CONVERSION_ID || 'AW-17677434119';

const GOOGLE_ADS_LABELS = {
  purchase: process.env.REACT_APP_GADS_LABEL_PURCHASE,
  signup: process.env.REACT_APP_GADS_LABEL_SIGNUP,
  beginCheckout: process.env.REACT_APP_GADS_LABEL_BEGIN_CHECKOUT,
  paymentInfo: process.env.REACT_APP_GADS_LABEL_PAYMENT_INFO,
};

function safeWindow() {
  return typeof window !== 'undefined' ? window : null;
}

export function pushDataLayer(payload) {
  const w = safeWindow();
  if (!w) return;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(payload);
}

export function trackFbEvent(eventName, params = {}, options = {}) {
  const w = safeWindow();
  if (!w || typeof w.fbq !== 'function') return;
  if (options.eventID) {
    w.fbq('track', eventName, params, { eventID: options.eventID });
  } else {
    w.fbq('track', eventName, params);
  }
}

export function trackFbCustomEvent(eventName, params = {}, options = {}) {
  const w = safeWindow();
  if (!w || typeof w.fbq !== 'function') return;
  if (options.eventID) {
    w.fbq('trackCustom', eventName, params, { eventID: options.eventID });
  } else {
    w.fbq('trackCustom', eventName, params);
  }
}

function normalizeEmail(email) {
  if (typeof email !== 'string') return undefined;
  const normalized = email.trim().toLowerCase();
  return normalized.includes('@') ? normalized : undefined;
}

/** Telefone em E.164 usando o DDI real do contato (Brasil quando não informado). */
function normalizePhone(phone, phoneDdi) {
  if (phone == null) return undefined;
  const e164 = toE164Digits(phone, phoneDdi);
  return e164.length >= 10 ? `+${e164}` : undefined;
}

function normalizeName(name) {
  if (typeof name !== 'string') return undefined;
  const normalized = name.trim().toLowerCase();
  return normalized || undefined;
}

/**
 * Conversões Otimizadas: envia dados próprios (hasheados pelo gtag no browser)
 * para recuperar atribuição perdida por cookies bloqueados.
 * Precisa estar habilitado em Google Ads > Conversões > Configurações.
 */
export function setGoogleAdsUserData({ email, phone, phoneDdi, firstName, lastName } = {}) {
  const w = safeWindow();
  if (!w || typeof w.gtag !== 'function') return;

  const userData = {};
  const normalizedEmail = normalizeEmail(email);
  const normalizedPhone = normalizePhone(phone, phoneDdi);
  if (normalizedEmail) userData.email = normalizedEmail;
  if (normalizedPhone) userData.phone_number = normalizedPhone;

  const address = {};
  const first = normalizeName(firstName);
  const last = normalizeName(lastName);
  if (first) address.first_name = first;
  if (last) address.last_name = last;
  if (Object.keys(address).length > 0) userData.address = address;

  if (Object.keys(userData).length === 0) return;
  w.gtag('set', 'user_data', userData);
}

function trackGoogleAdsConversion(labelKey, params = {}) {
  const w = safeWindow();
  const label = GOOGLE_ADS_LABELS[labelKey];
  if (!w || typeof w.gtag !== 'function' || !label) return;

  const payload = { send_to: `${GOOGLE_ADS_ID}/${label}` };
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) payload[key] = value;
  });
  w.gtag('event', 'conversion', payload);
}

function onceKey(key) {
  const w = safeWindow();
  if (!w) return false;
  const full = `${DEDUPE_PREFIX}${key}`;
  try {
    if (w.sessionStorage.getItem(full)) return false;
    w.sessionStorage.setItem(full, '1');
    return true;
  } catch {
    return true;
  }
}

/** Captura UTMs / fbclid / gclid na URL e persiste para atribuição. */
export function captureAttributionParams() {
  const w = safeWindow();
  if (!w) return;
  try {
    const params = new URLSearchParams(w.location.search);
    const keys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'fbclid',
      'gclid',
      'gbraid',
      'wbraid',
    ];
    const found = {};
    let hasAny = false;
    keys.forEach((k) => {
      const v = params.get(k);
      if (v) {
        found[k] = v;
        hasAny = true;
      }
    });
    if (!hasAny) return;
    const prev = JSON.parse(w.sessionStorage.getItem(UTM_STORAGE_KEY) || '{}');
    w.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify({ ...prev, ...found }));
  } catch {
    // ignore
  }
}

export function getAttributionParams() {
  const w = safeWindow();
  if (!w) return {};
  try {
    return JSON.parse(w.sessionStorage.getItem(UTM_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Acesso por link de campanha (ex.: botão do template WhatsApp -> /agendar?utm_campaign=...&ref=<contato>).
 * Dispara `campaign_landing` no GTM e relaya ao backend (CRM: "Acessou o link / Interesse").
 * Dedupe por campanha na sessão. `ref` é um UUID opaco do contato no WhatsWave.
 */
export function trackCampaignLanding({ pathname } = {}) {
  const w = safeWindow();
  if (!w) return;
  try {
    const params = new URLSearchParams(w.location.search);
    const campaign = params.get('utm_campaign');
    if (!campaign) return;

    const urlRef = params.get('ref');
    if (urlRef) w.sessionStorage.setItem(WA_REF_STORAGE_KEY, urlRef);
    const ref = urlRef || w.sessionStorage.getItem(WA_REF_STORAGE_KEY) || undefined;

    if (!onceKey(`campaign_landing_${campaign}`)) return;

    const attribution = getAttributionParams();
    const landingPath = pathname || w.location.pathname;
    const eventID = `campaign_landing_${campaign}_${Date.now()}`;

    pushDataLayer({
      event: 'campaign_landing',
      event_id: eventID,
      campaign_id: campaign,
      landing_path: landingPath,
      ...attribution,
    });
    trackFbCustomEvent('CampaignLanding', { campaign_id: campaign, ...attribution }, { eventID });

    sendLifecycleEvent('campaign.landing', {
      ref,
      landing_path: landingPath,
      attribution: pickAttribution(attribution),
    });
  } catch {
    // ignore
  }
}

/**
 * PageView virtual em mudanças de rota do SPA.
 * skipInitial: true evita duplicar o PageView do index.html no primeiro mount.
 */
export function trackSpaPageView(pathname, search = '', { skipInitial = false } = {}) {
  const w = safeWindow();
  if (!w) return;

  if (skipInitial && !w.__ddSpaPageviewReady) {
    w.__ddSpaPageviewReady = true;
    return;
  }
  w.__ddSpaPageviewReady = true;

  const page_path = `${pathname || '/'}${search || ''}`;
  const page_location = w.location.origin + page_path;

  pushDataLayer({
    event: 'virtual_page_view',
    page_path,
    page_location,
  });

  if (typeof w.fbq === 'function') {
    w.fbq('track', 'PageView');
  }

  if (typeof w.gtag === 'function') {
    w.gtag('event', 'page_view', {
      page_path,
      page_location,
    });
  }
}

export function trackCompleteRegistration({
  userId,
  method = 'email',
  email,
  phone,
  phoneDdi,
} = {}) {
  const dedupe = `signup_${userId || 'anon'}`;
  if (!onceKey(dedupe)) return;
  const eventID = `signup_${userId || Date.now()}`;
  const params = {
    content_name: 'sign_up',
    status: true,
    method,
    ...getAttributionParams(),
  };
  pushDataLayer({ event: 'complete_registration', event_id: eventID, ...params });
  trackFbEvent('CompleteRegistration', params, { eventID });
  // Campanhas otimizadas para Lead (conversão personalizada / objetivo Lead)
  trackFbEvent('Lead', { content_name: 'sign_up', ...getAttributionParams() }, { eventID: `lead_${eventID}` });

  // Google Ads: sinal secundário. Cadastro é gratuito, então não deve ser
  // marcado como ação principal no painel — serve só como observação.
  setGoogleAdsUserData({ email, phone, phoneDdi });
  trackGoogleAdsConversion('signup', { transaction_id: eventID });
}

/** Chegada à tela de checkout. Sinal de topo de funil, não de intenção de pagar. */
export function trackInitiateCheckout({ doctorId, appointmentType, value, currency = 'BRL' } = {}) {
  const dedupe = `initiate_${doctorId || 'x'}_${appointmentType || 'x'}`;
  if (!onceKey(dedupe)) return;
  const params = {
    content_name: 'checkout',
    content_category: appointmentType || undefined,
    value: value != null ? Number(value) : undefined,
    currency,
    ...getAttributionParams(),
  };
  pushDataLayer({ event: 'initiate_checkout', ...params });
  // Espelha no backend (listas + CRM do WhatsWave). Só identifica usuário logado.
  sendLifecycleEvent('checkout.started', {
    doctor_id: doctorId != null ? String(doctorId) : undefined,
    appointment_type: appointmentType || undefined,
    value: params.value,
    attribution: pickAttribution(getAttributionParams()),
  });
  trackFbEvent('InitiateCheckout', params);
  trackGoogleAdsConversion('beginCheckout', {
    value: params.value,
    currency,
  });
}

/**
 * Tentativa real de pagamento: o usuário preencheu os dados e enviou o
 * checkout. É o sinal de meio de funil que separa quem só abriu a página
 * de quem realmente tentou comprar.
 */
export function trackAddPaymentInfo({
  doctorId,
  appointmentType,
  value,
  paymentMethod,
  currency = 'BRL',
} = {}) {
  const params = {
    content_name: 'payment_info',
    content_category: appointmentType || undefined,
    value: value != null ? Number(value) : undefined,
    currency,
    payment_method: paymentMethod || undefined,
    doctor_id: doctorId != null ? String(doctorId) : undefined,
    ...getAttributionParams(),
  };
  pushDataLayer({ event: 'add_payment_info', ...params });
  trackFbEvent('AddPaymentInfo', params);
  trackGoogleAdsConversion('paymentInfo', {
    value: params.value,
    currency,
  });
}

export function trackPurchase({
  appointmentId,
  value,
  currency = 'BRL',
  appointmentType,
  doctorId,
  user,
} = {}) {
  if (!appointmentId) return;
  const dedupe = `purchase_${appointmentId}`;
  if (!onceKey(dedupe)) return;
  const eventID = `purchase_${appointmentId}`;
  const params = {
    content_name: 'appointment',
    content_ids: [String(appointmentId)],
    content_type: 'product',
    content_category: appointmentType || undefined,
    value: value != null ? Number(value) : undefined,
    currency,
    doctor_id: doctorId != null ? String(doctorId) : undefined,
    ...getAttributionParams(),
  };
  pushDataLayer({
    event: 'purchase',
    event_id: eventID,
    appointment_id: appointmentId,
    ...params,
  });
  trackFbEvent('Purchase', params, { eventID });

  // Evento de negócio para conversão personalizada do Dr. Derma.
  // Critério: somente após confirmar transaction_status === 'paid' no backend.
  pushDataLayer({
    event: 'appointment_completed',
    event_id: `appointment_completed_${appointmentId}`,
    appointment_id: String(appointmentId),
    appointment_type: appointmentType || undefined,
    doctor_id: doctorId != null ? String(doctorId) : undefined,
    value: params.value,
    currency,
    payment_status: 'paid',
    ...getAttributionParams(),
  });
  trackFbCustomEvent(
    'AppointmentCompleted',
    {
      appointment_id: String(appointmentId),
      appointment_type: appointmentType || undefined,
      doctor_id: doctorId != null ? String(doctorId) : undefined,
      value: params.value,
      currency,
      payment_status: 'paid',
    },
    { eventID: `appointment_completed_${appointmentId}` }
  );

  // Evento padrão Schedule ajuda otimização de agendamento
  trackFbEvent(
    'Schedule',
    {
      content_name: 'appointment',
      content_category: appointmentType || undefined,
      value: params.value,
      currency,
    },
    { eventID: `schedule_${appointmentId}` }
  );

  // Google Ads: única conversão que representa receita. Deve ser a ação
  // principal da conta, com valor e transaction_id para deduplicação.
  setGoogleAdsUserData({
    email: user?.email,
    phone: user?.telephone,
    phoneDdi: user?.telephone_ddi,
    firstName: user?.firstname,
    lastName: user?.lastname,
  });
  trackGoogleAdsConversion('purchase', {
    value: params.value,
    currency,
    transaction_id: String(appointmentId),
  });
}

/** Verifica se a rota é área administrativa (sem tracking Clarity). */
export function isAdminPath(pathname) {
  return typeof pathname === 'string' && pathname.startsWith('/admin');
}

/** Ativa ou nega consentimento do Microsoft Clarity (consentv2). */
export function setClarityConsent(enabled) {
  const w = safeWindow();
  if (!w || typeof w.clarity !== 'function') return;
  const status = enabled ? 'granted' : 'denied';
  w.clarity('consentv2', {
    ad_Storage: status,
    analytics_Storage: status,
  });
}

/** Injeta Microsoft Clarity se REACT_APP_CLARITY_PROJECT_ID estiver definido. */
export function initClarity() {
  const w = safeWindow();
  const projectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
  if (!w || !projectId || w.__ddClarityLoaded) return;
  w.__ddClarityLoaded = true;
  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = 'https://www.clarity.ms/tag/' + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(w, w.document, 'clarity', 'script', projectId);

  setClarityConsent(!isAdminPath(w.location.pathname));
}

export function buildObrigadoPath({
  appointmentId,
} = {}) {
  const params = new URLSearchParams();
  if (appointmentId) params.set('appointment_id', String(appointmentId));
  const qs = params.toString();
  return qs ? `/obrigado?${qs}` : '/obrigado';
}
